import { styled, Container, Box, useTheme, Typography, Icon, IconButton } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import Header from "./vertical/header/Header";
import HorizontalHeader from "../full/horizontal/header/Header";
import Sidebar from "./vertical/sidebar/Sidebar";
import Navigation from "./horizontal/navbar/Navbar";
import { useEffect, useRef, useState } from "react";
import { fetchGetData, handleCloseBookingIdAlert } from "../../helpers/Utils";
import { setShowBookingIdAlert, setUser, setWindowInnerWidth } from "../../store/userSlice";
import PreloaderCustom from "../../components/shared/PreloaderCustom";
import IntroModal from "../../components/shared/IntroModal";
import { identifyMixpanel, trackMixpanel } from "../../services/mixpanel";
import { LOCATION, PAGEVIEW } from "../../helpers/Config";
import { Alert } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CloseIcon from "@mui/icons-material/Close";

const MainWrapper = styled("div")(() => ({
  display: "flex",
  minHeight: "100vh",
  width: "100%",
}));

const PageWrapper = styled("div")(() => ({
  display: "flex",
  flexGrow: 1,
  paddingBottom: "60px",
  flexDirection: "column",
  zIndex: 1,
  width: "100%",
  backgroundColor: "transparent",
}));

const FullLayout = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const newSearchParams = new URLSearchParams(searchParams);
  const tokenSearchParams = searchParams.get("token");
  const locationSearchParams = searchParams.get("location");
  const customizer = useSelector((state) => state.customizer);
  const isPreloader = useSelector((state) => state.userSlice.isPreloader);
  const showBookingIdAlert = useSelector((state) => state.userSlice.showBookingIdAlert);
  const user = useSelector((state) => state.userSlice.user);
  const [isOpenIntroModal, setIsOpenIntroModal] = useState(false);
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  let token = localStorage.getItem("token");
  let currentBookingId = localStorage.getItem("bookingId");
  const currentBookingIdRef = useRef(localStorage.getItem("bookingId"));

  useEffect(() => {
    if (tokenSearchParams || token?.length) {
      if (tokenSearchParams) {
        localStorage.setItem("token", tokenSearchParams);
      }
      verifyToken(tokenSearchParams || token);
    } else {
      navigate("/auth/login");
    }

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      handleExit();
    };
  }, []);

  useEffect(() => {
    if (user?.id) {
      trackMixpanel(PAGEVIEW, { page: location.pathname });
    }
  }, [location, user]);

  useEffect(() => {
    if (locationSearchParams && user?.id) {
      dispatch(
        setUser({
          ...user,
          settings: [
            ...user?.settings.map((el) => {
              if (el.key === LOCATION) {
                return { ...el, value: locationSearchParams };
              }
              return el;
            }),
          ],
        })
      );
    }
  }, [locationSearchParams]);

  useEffect(() => {
    const handleBookingIdChange = () => {
      const newBookingId = localStorage.getItem("bookingId");
      if (newBookingId && newBookingId !== currentBookingIdRef.current) {
        currentBookingIdRef.current = newBookingId;
        dispatch(setShowBookingIdAlert(true));
      }
    };

    window.addEventListener("bookingIdUpdated", handleBookingIdChange);

    return () => {
      window.removeEventListener("bookingIdUpdated", handleBookingIdChange);
    };
  }, []);

  const handleResize = () => {
    dispatch(setWindowInnerWidth(window.innerWidth));
  };

  const verifyToken = async (token = null) => {
    if (token) {
      fetchGetData(`/auth/me`).then((res) => {
        if (res?.id) {
          dispatch(setUser(res));
          handleLoginApp(token);
          handleCheckFirstLogin();
          handleLoginMixpanel(res);
        } else {
          handleExit();
        }
      });
    } else {
      handleExit();
    }
  };

  const handleLoginMixpanel = (user) => {
    const deviceInfo = getDeviceInfo();
    const networkInfo = getNetworkInfo();

    identifyMixpanel(user.id, {
      $name: `${user.firstName} ${user.lastName}`,
      $email: user.email,
      role: user.role,
      createdAt: user.createdAt,
      deviceBrand: deviceInfo.brand,
      deviceModel: deviceInfo.model,
      androidVersion: deviceInfo.androidVersion,
      appCrashes: deviceInfo.crashes,
      networkName: networkInfo.name,
      networkId: networkInfo.id,
      dataUsage: networkInfo.dataUsage,
      imsi: "Restricted",
      imei: "Restricted",
    });
  };

  const getDeviceInfo = () => {
    const userAgent = navigator.userAgent;
    return {
      brand: getBrandFromUserAgent(userAgent),
      model: "Unknown",
      androidVersion: userAgent.match(/Android (\d+\.\d+)/)?.[1] || "Unknown",
      crashes: parseInt(localStorage.getItem("app_crashes")) || 0,
    };
  };

  const getBrandFromUserAgent = (userAgent) => {
    if (/Samsung/i.test(userAgent)) return "Samsung";
    if (/Xiaomi|Mi\s|Redmi/i.test(userAgent)) return "Xiaomi";
    if (/Huawei|Honor/i.test(userAgent)) return "Huawei";
    if (/OnePlus/i.test(userAgent)) return "OnePlus";
    if (/Pixel|Google/i.test(userAgent)) return "Google Pixel";
    if (/OPPO/i.test(userAgent)) return "OPPO";
    if (/Vivo/i.test(userAgent)) return "Vivo";
    if (/Realme/i.test(userAgent)) return "Realme";
    if (/Motorola|Moto/i.test(userAgent)) return "Motorola";
    if (/Nokia/i.test(userAgent)) return "Nokia";
    if (/Sony/i.test(userAgent)) return "Sony";
    if (/Asus|ROG/i.test(userAgent)) return "Asus";
    if (/Lenovo/i.test(userAgent)) return "Lenovo";
    if (/ZTE/i.test(userAgent)) return "ZTE";
    if (/Meizu/i.test(userAgent)) return "Meizu";
    if (/LG/i.test(userAgent)) return "LG";
    if (/Alcatel/i.test(userAgent)) return "Alcatel";
    if (/Tecno/i.test(userAgent)) return "Tecno";
    if (/Infinix/i.test(userAgent)) return "Infinix";
    if (/Itel/i.test(userAgent)) return "Itel";
    if (/BlackBerry/i.test(userAgent)) return "BlackBerry";
    if (/Coolpad/i.test(userAgent)) return "Coolpad";
    if (/Micromax/i.test(userAgent)) return "Micromax";
    if (/Panasonic/i.test(userAgent)) return "Panasonic";
    if (/Sharp/i.test(userAgent)) return "Sharp";
    if (/Gionee/i.test(userAgent)) return "Gionee";
    return "Unknown";
  };

  const getNetworkInfo = () => {
    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;
    return {
      name: connection?.type || "Unknown",
      id: connection?.effectiveType || "Unknown",
      dataUsage: connection?.downlink || "Unknown",
    };
  };

  const handleLoginApp = (token) => {
    if (window.flutter_inappwebview) {
      window.flutter_inappwebview.callHandler("onLogin", token);
    }
  };

  const handleExit = () => {
    if (window.flutter_inappwebview) {
      window.flutter_inappwebview.callHandler("onLogout", "");
    }
    localStorage.removeItem("token");
    localStorage.removeItem("notFirstLogin");
    navigate("/auth/login");
  };

  const handleCheckFirstLogin = () => {
    let notFirstLogin = localStorage.getItem("notFirstLogin");
    if (!notFirstLogin) {
      // setIsOpenIntroModal(true);   // TODO: Delete this line if you don't want to show the intro modal
      localStorage.setItem("notFirstLogin", true);
    }
  };

  return (
    <MainWrapper
      className={
        customizer.activeMode === "dark" ? "darkbg mainwrapper" : "mainwrapper"
      }
      sx={{
        flexDirection: "column",
      }}
    >
      <IntroModal
        open={isOpenIntroModal}
        handleClose={() => setIsOpenIntroModal(false)}
      />
      {isPreloader && <PreloaderCustom />}
      {showBookingIdAlert && (
        <Alert
          icon={false}
          sx={{
            position: "fixed",
            top: 87,
            left: "50%",
            transform: "translateX(-50%)",
            width: "calc(100vw - 100px)",
            maxWidth: "320px",
            zIndex: 1300,
            borderRadius: "8px",
            bgcolor: "#E53935",
            color: "white",
            display: "flex",
            alignItems: "center",
            padding: "1.4px 20px",
            m: 0,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            width="100%"
            justifyContent="space-between"
            m={0}
          >
            <Box display="flex" alignItems="center" gap={1.5} mr={1}>
              <ErrorOutlineIcon fontSize="small" sx={{ color: "white" }} />
              <Typography fontSize={12} fontWeight={400}>
                Live session ready:
              </Typography>
            </Box>
            <NavLink
              underline="hover"
              style={{
                color: "white",
                fontSize: 12,
                fontWeight: 400,
                textDecoration: "underline",
              }}
              to={`/travel/streaming/${currentBookingId}`}
              onClick={() => handleCloseBookingIdAlert(dispatch)}
            >
              Go now
            </NavLink>
          </Box>
          <IconButton
            onClick={() => handleCloseBookingIdAlert(dispatch)}
            sx={{
              position: "absolute",
              right: 0,
              top: 0,
              color: "white",
              width: 24,
              height: 24,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Alert>
      )}
      {/* ------------------------------------------- */}
      {/* Sidebar */}
      {/* ------------------------------------------- */}
      {customizer.isHorizontal ? "" : <Sidebar />}
      {/* ------------------------------------------- */}
      {/* Main Wrapper */}

      {customizer.isHorizontal ? <HorizontalHeader /> : <Header />}
      {customizer.isHorizontal ? <Navigation /> : ""}
      {/* ------------------------------------------- */}
      <PageWrapper
        className="page-wrapper"
        sx={{
          margin: "0 auto",
          paddingBottom: "0px",
          // maxWidth: windowInnerWidth < 1200 ? '100%!important' : customizer.isCollapse ? 'calc(100% - 87px)!important' : 'calc(100% - 270px)!important',
          width: "100%",
          maxWidth: "1440px",
          ...(customizer.isCollapse && {
            [theme.breakpoints.up("lg")]: {
              ml: `${customizer.MiniSidebarWidth}px`,
            },
          }),
        }}
      >
        {/* ------------------------------------------- */}
        {/* Header */}
        {/* ------------------------------------------- */}
        {/* {customizer.isHorizontal ? <HorizontalHeader /> : <Header />} */}
        {/* ------------------------------------------- */}
        {/* PageContent */}
        {/* ------------------------------------------- */}
        {/* {customizer.isHorizontal ? <Navigation /> : ''} */}
        <Container
          sx={{
            maxWidth: customizer.isLayout === "boxed" ? "lg" : "100%!important",
            // pl: customizer.isLayout === 'boxed' ? 6 : 3,
            // pr: customizer.isLayout === 'boxed' ? 6 : 3,
            pl: customizer.isLayout === "boxed" ? 0 : 0,
            pr: customizer.isLayout === "boxed" ? 0 : 0,
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* ------------------------------------------- */}
          {/* Page Route */}
          {/* ------------------------------------------- */}
          {/* <Box sx={{ minHeight: 'calc(100vh - 170px)' }}> */}
          {/* <Box sx={{ minHeight: 'calc(100vh - 295px)' }}> */}
          <Box display={"flex"} flexDirection={"column"} sx={{ flex: 1 }}>
            <Outlet />
          </Box>
          {/* ------------------------------------------- */}
          {/* End Page */}
          {/* ------------------------------------------- */}
        </Container>
        {/* <Customizer /> */}
      </PageWrapper>
      {/* <Footer /> */}
    </MainWrapper>
  );
};

export default FullLayout;
